// NPM MODULES
import React, { useState, useEffect } from 'react';
import {
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { DateTime } from 'luxon';

import Loader from '../../common/Loader';

// NETWORKING
import useUserReportRequest from '../../../requests/UserReportRequest';

// FORMATTED MESSAGES
import UserReportsFormattedMessages from '../../../messages/reports/UserReportsFormattedMessages';

const SingleViewPending = ({
  user,
  fromDate,
  toDate,
  errorHandler,
  setPageTitle,
}) => {
  const {
    getMessagesForId,
    cancelAllPendingMessages,
    cancelSinglePendingMessage,
  } = useUserReportRequest();
  const [messages, setMessages] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPageTitle(UserReportsFormattedMessages.pendingReminders);
    const fetchMessages = async () => {
      try {
        if (user) {
          const messages = await getMessagesForId(
            user.customer_user_id,
            'REMPEN',
            fromDate.toMillis(),
            toDate.toMillis(),
            errorHandler
          );
          setMessages(messages);
        } else {
          setHasError(true);
          setErrorMessage('Expected User in Props');
        }
        setLoading(false);
      } catch (e) {
        setHasError(true);
        setLoading(false);
        setErrorMessage('Network Request Failed.');
      }
    };

    fetchMessages();
  }, [user, fromDate, toDate, errorHandler]);

  // Handles the click for "Cancel All Pending" button.
  const handleCancelPendingConfirmation = () => {
    setShowConfirmation(true);
  };

  const handleCancelModal = (shouldCancel) => {
    setShowConfirmation(shouldCancel);
  };

  // Handles the click for "Cancel All Pending" button.
  const handleCancelPending = async () => {
    try {
      await cancelAllPendingMessages(
        user.customer_user_id,
        'REMPEN',
        errorHandler
      );

      // Refresh the page to update status of any pending items
      const messages = await getMessagesForId(
        user.customer_user_id,
        'REMPEN',
        fromDate,
        toDate
      );
      setMessages(messages);
      setShowConfirmation(false);
    } catch (e) {
      setHasError(true);
      setErrorMessage(e.message);
      setShowConfirmation(false);
    }
  };

  // Cancels a single Pending Reminder
  const onClickCancelSingleReminder = async (schedId) => {
    try {
      await cancelSinglePendingMessage(
        user.customer_user_id,
        schedId,
        errorHandler
      );

      // Refresh the page to update status of any pending items
      const messages = await getMessagesForId(
        user.customer_user_id,
        'REMPEN',
        fromDate,
        toDate
      );
      setMessages(messages);
      setShowConfirmation(false);
    } catch (e) {
      setHasError(true);
      setErrorMessage(e.message);
      setShowConfirmation(false);
    }
  };

  // Adding the time range text fromDate to toDate
  const generateTimeRangeText = () => {
    const formattedFromDate = moment(fromDate.toMillis()).format(
      'DD MMM YYYY h:mm:ss A'
    );
    const formattedToDate = moment(toDate.toMillis()).format(
      'DD MMM YYYY h:mm:ss A'
    );

    return (
      <Typography gutterBottom>
        {UserReportsFormattedMessages.from} {formattedFromDate}{' '}
        {UserReportsFormattedMessages.to} {formattedToDate}
      </Typography>
    );
  };

  const generateTable = () => {
    return (
      <TableContainer
        sx={{
          marginBottom: 4,
          overflowX: 'auto',
          width: '100%',
          whiteSpace: 'nowrap',
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="right" colSpan={12}>
                {UserReportsFormattedMessages.messagesTitle}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                align="right"
                sx={{ verticalAlign: 'top', borderRight: '1px solid' }}
                rowSpan={2}
              >
                {UserReportsFormattedMessages.typeText}
              </TableCell>
              <TableCell
                align="right"
                sx={{ verticalAlign: 'top', borderRight: '1px solid' }}
                rowSpan={2}
              >
                {UserReportsFormattedMessages.phoneNumberText}
              </TableCell>
              <TableCell
                align="right"
                sx={{ verticalAlign: 'top', borderRight: '1px solid' }}
                rowSpan={2}
              >
                {UserReportsFormattedMessages.countryText}
              </TableCell>
              <TableCell
                align="right"
                sx={{ verticalAlign: 'top', borderRight: '1px solid' }}
                rowSpan={2}
              >
                {UserReportsFormattedMessages.localFlagText}
              </TableCell>
              <TableCell
                align="right"
                sx={{ verticalAlign: 'top', borderRight: '1px solid' }}
                colSpan={3}
              >
                Due
              </TableCell>
              <TableCell align="right" colSpan={3}>
                Received
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="right">
                {UserReportsFormattedMessages.dayText}
              </TableCell>
              <TableCell align="right">
                {UserReportsFormattedMessages.dateText}
              </TableCell>
              <TableCell align="right">
                {UserReportsFormattedMessages.timeText}
              </TableCell>
              <TableCell align="right">
                {UserReportsFormattedMessages.dayText}
              </TableCell>
              <TableCell align="right">
                {UserReportsFormattedMessages.dateText}
              </TableCell>
              <TableCell align="right">
                {UserReportsFormattedMessages.timeText}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{generateTableBody()}</TableBody>
        </Table>
      </TableContainer>
    );
  };

  const generateTableBody = () => {
    if (!messages || messages.length === 0) return null;
    return messages.map((msg) => {
      const sentDate = DateTime.fromISO(msg.sms_sent);
      const rezoned = sentDate.setZone(localStorage.getItem('timeShort'), {
        keepLocalTime: false,
      });
      const day = rezoned.toFormat('EEEE');
      const date = rezoned.toFormat('LLL dd, yyyy');
      const time = rezoned.toFormat('h:mm:ss a');
      const nextSend = DateTime.fromISO(msg.next_send);
      const rezonedTwo = nextSend.setZone(localStorage.getItem('timeShort'), {
        keepLocalTime: false,
      });
      const nextSendDay = rezonedTwo.toFormat('EEEE');
      const nextSendDate = rezonedTwo.toFormat('LLL dd, yyyy');
      const nextSendTime = rezonedTwo.toFormat('h:mm:ss a');

      return (
        <TableRow key={`list${msg.sched_id}`} id={msg.sched_id}>
          <TableCell align="right" name="type">
            <span
              className="blue"
              onClick={() => onClickCancelSingleReminder(msg.sched_id)}
            >
              {msg.recurring === 'RR' ? 'Recurring' : 'Non Recurring'}
            </span>
          </TableCell>
          <TableCell align="right" name="dest_tn">
            {msg.dest_tn}
          </TableCell>
          <TableCell align="right" name="country">
            {msg.country_code_name}
          </TableCell>
          <TableCell align="right" name="local_flag">
            {msg.local_flag}
          </TableCell>
          <TableCell align="right" name="day">
            {nextSendDay}
          </TableCell>
          <TableCell align="right" name="date">
            {nextSendDate}
          </TableCell>
          <TableCell align="right" name="time">
            {nextSendTime}
          </TableCell>
          <TableCell align="right" name="day">
            {day}
          </TableCell>
          <TableCell align="right" name="date">
            {date}
          </TableCell>
          <TableCell align="right" name="time">
            {time}
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <Box>
      {generateTimeRangeText()}
      {loading ? <Loader /> : <>{generateTable()}</>}
      {hasError && <Alert severity="error">{errorMessage}</Alert>}
    </Box>
  );
};

export default SingleViewPending;
